import { useCallback, useState } from "react";

import { FilePaperFill } from "@vericus/cadmus-icons";
import {
  Button,
  Divider,
  Input,
  Modal,
  Text,
  TextArea,
  vars,
  VisuallyHidden,
} from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";
import { UseFormReturnType } from "@mantine/form";

import { ProjectIssueFragment, ProjectIssueInput } from "@/generated/graphql";

import { FormState, useIssueForm } from "../../hooks/use-issue-form";
import { ProjectIssuesTable } from "../../hooks/use-project-issues-table";
import { getProjectIssueInput } from "../../project-management-board";
import { ProjectIssueWithChildren } from "../../types";
import { statusOptions } from "../../utils";
import { AssigneeSelect } from "./assignee-select";
import { buttonContainer, formGroup, formRow, label } from "./issue-form.css";
import { SimpleDropdown } from "./simple-dropdown";

/**
 * Props for the IssueForm component.
 */
interface IssueFormProp {
  table: ProjectIssuesTable;
  currentIssue?: ProjectIssueFragment;
  currentIssueParent?: ProjectIssueWithChildren;
  milestoneOptions: { value: string; text: string }[];
  memberOptions: { value: string; label: string }[];
  assessmentDueDate: Date;

  milestoneOptionsEnabled: boolean;

  form: UseFormReturnType<FormState, (values: FormState) => FormState>;

  onSaveIssue: (input: ProjectIssueInput[]) => void;
  updateIssue: (updatedProjectIssue: ProjectIssueFragment) => void;
}

interface IssueFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const IssueFormModal = create<IssueFormProp>((props) => {
  const modal = useModal();

  const { form, updateProjectIssue, resetForm } = useIssueForm(
    props.currentIssue,
    props.milestoneOptionsEnabled
  );

  const handleSaveIssue = useCallback(
    (input: ProjectIssueInput[]) => {
      props.onSaveIssue(input);
      resetForm();
      modal.remove();
    },
    [props, resetForm, modal]
  );

  return (
    <IssueForm
      table={props.table}
      open={modal.visible}
      form={form}
      onOpenChange={(open: boolean) => {
        if (open) {
          modal.show();
        } else {
          resetForm();
          modal.remove();
        }
      }}
      currentIssue={props.currentIssue}
      currentIssueParent={props.currentIssueParent}
      onSaveIssue={handleSaveIssue}
      updateIssue={updateProjectIssue}
      milestoneOptions={props.milestoneOptions}
      memberOptions={props.memberOptions}
      milestoneOptionsEnabled={props.milestoneOptionsEnabled}
      assessmentDueDate={props.assessmentDueDate}
    />
  );
});

export function IssueForm({
  open,
  form,
  onOpenChange,
  currentIssue,
  onSaveIssue,
  updateIssue,
  milestoneOptions,
  memberOptions,
  milestoneOptionsEnabled,
  assessmentDueDate,
}: IssueFormProp & IssueFormModalProps) {
  const issueFormState = form.getValues().issueFormState;
  const errors = form.errors;

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateIssue({
        title: e.target.value,
      } as ProjectIssueFragment);
      // Clear title error when user starts typing
      if (errors["issueFormState.title"]) {
        form.clearFieldError("issueFormState.title");
      }
    },
    [updateIssue, errors, form]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateIssue({
        description: e.target.value,
      } as ProjectIssueFragment);
      // Clear description error when user starts typing
      if (errors["issueFormState.description"]) {
        form.clearFieldError("issueFormState.description");
      }
    },
    [updateIssue, errors, form]
  );

  const handleAssigneeSelect = useCallback(
    (id: string[]) => {
      updateIssue({
        assignees: id,
      } as ProjectIssueFragment);
      // Clear assignee error when user selects
      if (errors["issueFormState.assignees"]) {
        form.clearFieldError("issueFormState.assignees");
      }
    },
    [updateIssue, errors, form]
  );

  const handleDueDateChange = useCallback(
    (date: Date | null) => {
      updateIssue({ dueDate: date } as ProjectIssueFragment);
      // Clear due date error when user selects
      if (errors["issueFormState.dueDate"]) {
        form.clearFieldError("issueFormState.dueDate");
      }
    },
    [updateIssue, errors, form]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      updateIssue({ status: value } as ProjectIssueFragment);
      // Clear status error when user selects
      if (errors["issueFormState.status"]) {
        form.clearFieldError("issueFormState.status");
      }
    },
    [updateIssue, errors, form]
  );

  const handleMilestoneChange = useCallback(
    (value: string) => {
      updateIssue({
        parentId: value,
      } as ProjectIssueFragment);
      // Clear milestone error when user selects
      if (errors["issueFormState.parentId"]) {
        form.clearFieldError("issueFormState.parentId");
      }
    },
    [updateIssue, errors, form]
  );

  const handleSave = useCallback(() => {
    // Validate form before saving
    const validation = form.validate();

    if (!validation.hasErrors) {
      const input = getProjectIssueInput(issueFormState);
      onSaveIssue([input]);
    }
  }, [form, issueFormState, onSaveIssue]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const [focused, setIsFocused] = useState(false);

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange}>
      <Modal.Content style={{ width: 1050 }}>
        <VisuallyHidden.VisuallyHidden>
          <Modal.Title />
          <Modal.Description />
        </VisuallyHidden.VisuallyHidden>

        <div
          style={{
            display: "flex",
            gap: 16,
            justifyContent: "left",
            padding: "24px 40px",
            margin: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              padding: "15px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderRadius: "1em",
              border: `1px solid ${vars.colors.navy300}`,
            }}
          >
            <FilePaperFill size={24} label="task-filling" />
          </div>
          <div>
            <Text kind="headingFive" as="div">
              {issueFormState.title || "New Task"}
            </Text>
            <Text
              kind="systemMd"
              as="div"
              style={{
                maxWidth: "500px",
                maxHeight: "50px",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {issueFormState.description || "No description"}
            </Text>
          </div>
        </div>

        <Divider />

        <Text kind="headingSix" as="div" style={{ padding: "20px 40px" }}>
          Overview
        </Text>

        <Divider />

        <div style={{ padding: "20px 40px" }}>
          <div className={formGroup}>
            <Text kind="systemMd">Task Name *</Text>
            <Input
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              fullWidth
              placeholder="Enter task name"
              value={issueFormState.title || ""}
              onChange={handleTitleChange}
              error={!!errors["issueFormState.title"]}
              style={{
                boxShadow: focused
                  ? `0 0 0 1px ${vars.colors.primary}`
                  : "none",
              }}
            />
            {errors["issueFormState.title"] && (
              <Text
                kind="systemSm"
                style={{ color: vars.colors.red500, marginTop: "4px" }}
              >
                {errors["issueFormState.title"]}
              </Text>
            )}
          </div>

          <div className={formGroup}>
            <Text kind="systemMd">Description</Text>
            <TextArea.Input
              placeholder="Add note"
              minRows={3}
              value={issueFormState.description || ""}
              style={{ padding: "10px 16px" }}
              maxLength={200}
              maxRows={10}
              onChange={handleDescriptionChange}
            />
            {errors["issueFormState.description"] && (
              <Text
                kind="systemSm"
                style={{ color: vars.colors.red500, marginTop: "4px" }}
              >
                {errors["issueFormState.description"]}
              </Text>
            )}
          </div>

          <div className={formRow}>
            <div className={formGroup} style={{ flex: 1 }}>
              <label className={label}>Assignee</label>
              <AssigneeSelect
                items={memberOptions}
                placeholder="Search members..."
                selectedItemId={issueFormState.assignees?.at(0)}
                onSelectItem={handleAssigneeSelect}
              />
              {errors["issueFormState.assignees"] && (
                <Text
                  kind="systemSm"
                  style={{ color: vars.colors.red500, marginTop: "4px" }}
                >
                  {errors["issueFormState.assignees"]}
                </Text>
              )}
            </div>

            <div className={formGroup} style={{ flex: 1 }}>
              <label className={label}>Due Date</label>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                }}
              >
                <input
                  type="date"
                  value={
                    issueFormState.dueDate
                      ? new Date(issueFormState.dueDate)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={(e) => {
                    const dateValue = e.target.value
                      ? new Date(e.target.value)
                      : null;
                    handleDueDateChange(dateValue);
                  }}
                  min={new Date().toISOString().split("T")[0]}
                  max={new Date(assessmentDueDate).toISOString().split("T")[0]}
                  style={{
                    width: "100%",
                    padding: 12,
                    border: errors["issueFormState.dueDate"]
                      ? "1px solid #ef4444"
                      : "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    outline: "none",
                    backgroundColor: "white",
                  }}
                  placeholder="Select date"
                />
              </div>
              {errors["issueFormState.dueDate"] && (
                <Text
                  kind="systemSm"
                  style={{ color: vars.colors.red500, marginTop: "4px" }}
                >
                  {errors["issueFormState.dueDate"]}
                </Text>
              )}
            </div>
          </div>

          <div className={formRow}>
            <div className={formGroup} style={{ flex: 1 }}>
              <label className={label}>Status</label>

              <SimpleDropdown
                selected={issueFormState.status}
                items={statusOptions}
                onSelect={handleStatusChange}
                kind="status"
              />
              {errors["issueFormState.status"] && (
                <Text
                  kind="systemSm"
                  style={{ color: vars.colors.red500, marginTop: "4px" }}
                >
                  {errors["issueFormState.status"]}
                </Text>
              )}
            </div>

            <div className={formGroup} style={{ flex: 1 }}>
              {milestoneOptionsEnabled && (
                <>
                  <label className={label}>Select Milestone *</label>
                  <SimpleDropdown
                    selected={issueFormState.parentId || ""}
                    items={milestoneOptions}
                    onSelect={handleMilestoneChange}
                    kind="milestone"
                  />
                  {errors["issueFormState.parentId"] && (
                    <Text
                      kind="systemSm"
                      style={{ color: vars.colors.red500, marginTop: "4px" }}
                    >
                      {errors["issueFormState.parentId"]}
                    </Text>
                  )}
                </>
              )}
            </div>
          </div>

          <Divider />

          <div className={buttonContainer}>
            <Button kind="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} kind="primary">
              {currentIssue ? "Update Task" : "Create Task"}
            </Button>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
}
